// src/core/telemetry.ts
import * as Sentry from '@sentry/browser';

type TelemetryEvent = {
  type: string;
  at: string;
  payload?: Record<string, unknown>;
};

let apiUrl = '';
let token = '';
let clientId = 0;
let initialized = false;
const buffer: TelemetryEvent[] = [];
const FLUSH_INTERVAL_MS = 30_000;
const MAX_BUFFER = 100;
let flushTimer: ReturnType<typeof setInterval> | null = null;

export function init(opts: { apiUrl: string; token: string; clientId: number }): void {
  apiUrl = opts.apiUrl;
  token = opts.token;
  clientId = opts.clientId;
  initialized = true;

  const dsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined) ?? '';
  if (dsn) {
    Sentry.init({
      dsn,
      environment: (import.meta.env.VITE_ENV as string | undefined) ?? 'dev',
      release: `cp-widget@${(import.meta.env.VITE_VERSION as string | undefined) ?? '0.0.0'}`,
      tracesSampleRate: 0,
      sampleRate: 1.0,
      ignoreErrors: [
        /ResizeObserver loop limit exceeded/,
        /Non-Error promise rejection captured/,
      ],
      beforeSend(event) {
        if (event.request?.url) {
          try {
            const u = new URL(event.request.url);
            event.request.url = `${u.origin}${u.pathname}`;
          } catch { /* ignore */ }
        }
        return event;
      },
    });
    Sentry.setTag('partner_token', opts.token);
  }

  if (!flushTimer) {
    flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', flushSync);
    // pagehide для iOS Safari (надёжнее beforeunload на мобильных)
    window.addEventListener('pagehide', flushSync);
  }
}

export function track(type: string, payload?: Record<string, unknown>): void {
  if (!initialized || !apiUrl || !token) return;
  buffer.push({ type, at: new Date().toISOString(), payload });
  if (buffer.length >= MAX_BUFFER) flush();
}

export function debug(...args: unknown[]): void {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[cpw]', ...args);
  }
}

export function flush(): void {
  if (buffer.length === 0) return;
  const events = buffer.splice(0);
  fetch(`${apiUrl}/api/widget/telemetry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, clientId, events }),
    keepalive: true,
  }).catch(() => {
    // Requeue at head для повторной попытки на следующем flush
    buffer.unshift(...events);
  });
}

function flushSync(): void {
  if (buffer.length === 0 || !apiUrl || !token) return;
  const events = buffer.splice(0);
  const payload = JSON.stringify({ token, clientId, events });
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([payload], { type: 'application/json' });
    navigator.sendBeacon(`${apiUrl}/api/widget/telemetry`, blob);
  } else {
    // fallback — fire-and-forget fetch (может не доставиться)
    fetch(`${apiUrl}/api/widget/telemetry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  }
}
